import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import ReviewForm from './ReviewForm';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewSection = ({ garmentId, garmentName }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchReviews();
  }, [garmentId]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reviews/garment/${garmentId}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmitted = () => {
    setShowForm(false);
    fetchReviews();
  };

  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${index < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="mt-8" data-testid="review-section">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-2xl font-bold flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-black" />
            Opiniones de compradores
          </h3>
          {reviews.length > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <div className="flex">{renderStars(Math.round(getAverageRating()))}</div>
              <span className="text-lg font-semibold">{getAverageRating()}</span>
              <span className="text-gray-500">({reviews.length} {reviews.length === 1 ? 'reseña' : 'reseñas'})</span>
            </div>
          )}
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-black hover:bg-neutral-800"
          data-testid="write-review-button"
        >
          Escribir reseña
        </Button>
      </div>

      {showForm && (
        <ReviewForm
          garmentId={garmentId}
          garmentName={garmentName}
          onSubmitted={handleReviewSubmitted}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
      ) : reviews.length === 0 ? (
        <Card className="p-8 text-center">
          <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">Aún no hay reseñas para este producto.</p>
          <p className="text-gray-500 text-sm mt-2">¡Sé el primero en compartir tu opinión!</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map(review => (
            <Card key={review.id} className="p-6" data-testid={`review-${review.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-semibold text-lg">{review.customer_name}</p>
                  <p className="text-sm text-gray-500">{formatDate(review.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex">{renderStars(review.rating)}</div>
                  {review.verified_purchase && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      ✓ Compra verificada
                    </span>
                  )}
                </div>
              </div>
              <p className="text-gray-700">{review.comment}</p>
              
              {/* Mostrar fotos de la reseña */}
              {review.photos && review.photos.length > 0 && (
                <div className="flex gap-2 mt-4 flex-wrap">
                  {review.photos.map((photo, idx) => (
                    <img
                      key={idx}
                      src={photo}
                      alt={`Foto ${idx + 1} de ${review.customer_name}`}
                      className="w-24 h-24 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition"
                      onClick={() => window.open(photo, '_blank')}
                    />
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewSection;